import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationAction,
  AutomationActionDenormalized,
  AutomationActionTemplate,
  ConcreteExperienceMessage,
  IAutomationTemplate,
  IExperience,
  IExperienceTemplate,
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import {
  AssigneeBouncedErrorData,
  AssingSupportingActorErrorData,
  CCMissingEmailErrorData,
  ErroDataType,
  SalesforceContactResolutionFailedErrorData,
  SalesforceWriteFailedErrorData,
  SendBackgroundCheckErrorData,
  CompleteLmsModuleErrorData,
  SendDocusignEnvelopeErrorData
} from '@dadohr/shared/domainTypes/instance';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  ScheduleChannel,
  ScheduleChannelType
} from '@dadohr/shared/domainTypes/schedules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import { Checkbox } from '@material-ui/core';
import { compact, flatten, uniq } from 'lodash';
import { useCallback, useMemo } from 'react';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import {
  ItemSorter,
  ItemSorters,
  RowsRenderer,
  ROW_HEIGHTS
} from '../../../../components/GroupableList';
import { Loader } from '../../../../components/Loader';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { useSort } from '../../../../hooks/useSort';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { getParticipantActor } from '../../../../services/experiences/instance';
import { useMappedLoadingValue } from '../../../../services/firebase';
import {
  formatDatePrecise,
  ONE_MINUTE,
  toMomentFromAny
} from '../../../../services/time';
import { toFullName, useUserMapById } from '../../../../services/user';
import {
  IssueDetails,
  IssueTitle,
  SuggestedActionForAssigneeBounce,
  SuggestedActionForCCError,
  SuggestedActionForError,
  SuggestedActionForInstanceSubtaskError,
  SuggestedActionForMissingData,
  SuggestedActionForSentItem
} from './ParticipantDialog';
import { collectAutomationIssues } from './issues/collectors/automation';
import { collectCCIssues } from './issues/collectors/cc';
import { collectAssigneeBounceIssues } from './issues/collectors/assigneeBounce';
import { collectInstanceIssues } from './issues/collectors/instanceSubtask';
import { collectSentItemIssues } from './issues/collectors/sentItem';
import { collectMissingSupportingActors } from './issues/collectors/missingSupportingActors';
import { collectMissingVariables } from './issues/collectors/missingVariables';

export type AutomationActionErrorData = {
  type: 'AUTOMATION';
  tpl: Doc<IExperienceTemplate>;
  instance: Doc<IExperience>;
  id: string;
  automationTpl: IAutomationTemplate;
  actionTpl: AutomationActionTemplate;
  action: AutomationAction;
  denormalized: AutomationActionDenormalized;
};

export type SentItemErrorData = {
  type: 'SENT_ITEM';
  tpl: Doc<IExperienceTemplate>;
  instance: Doc<IExperience>;
  id: string;
  includedItems: (
    | {
        type: 'MESSAGE';
        tplId: string;
        title: string;
      }
    | {
        type: 'TASK';
        tplId: string;
        title: string;
      }
  )[];
  /** The triggering message/task's own template id — the message-driven
   * path already resolves this locally (`messagesByTplId` lookup) to build
   * `humanReadableTitle`; the task-driven path resolves the equivalent via
   * `firstTask.tpl.id`. Provides a stable, cross-instance id to key on. Note
   * that `includedItems` doesn't actually hold this info: it's always an
   * empty array. */
  itemTplId: string;
  humanReadableTitle: string;
  actor: string;
  message: ConcreteExperienceMessage;
  channels: ScheduleChannel[];
};

export type MissingActorErrorData = {
  type: 'MISSING_ACTOR';
  tpl: Doc<IExperienceTemplate>;
  instance: Doc<IExperience>;
  id: string;
  actor: ISupportingActor;
};

export type MissingVariableErrorData = {
  type: 'MISSING_VAR';
  tpl: Doc<IExperienceTemplate>;
  instance: Doc<IExperience>;
  id: string;
  missingVar: string[];
};

export type InstanceErrorDataBase = {
  instance: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  timelineItem: string;
  // Set when this row collapses multiple identical instanceErrors entries
  // (e.g. the same Salesforce upsert failing on every worker retry). The row
  // shows "×N"; ignoring it flips `ignore` on every entry in `groupedErrorIds`.
  // Absent / 1 for non-collapsed rows.
  occurrenceCount?: number;
  groupedErrorIds?: string[];
};

export type AssignSupprotingActorErroWithData = AssingSupportingActorErrorData &
  InstanceErrorDataBase;
export type SendDocusignEnvelopeErrorWithData = SendDocusignEnvelopeErrorData &
  InstanceErrorDataBase;
export type SalesforceContactResolutionFailedErrorWithData = SalesforceContactResolutionFailedErrorData &
  InstanceErrorDataBase;
export type SalesforceWriteFailedErrorWithData = SalesforceWriteFailedErrorData &
  InstanceErrorDataBase;
export type SendBackgroundCheckErrorWithData = SendBackgroundCheckErrorData &
  InstanceErrorDataBase;
export type CompleteLmsModuleErrorWithData = CompleteLmsModuleErrorData &
  InstanceErrorDataBase;
export type InstanceErroWithData =
  | AssignSupprotingActorErroWithData
  | SendDocusignEnvelopeErrorWithData
  | SalesforceContactResolutionFailedErrorWithData
  | SalesforceWriteFailedErrorWithData
  | SendBackgroundCheckErrorWithData
  | CompleteLmsModuleErrorWithData;

export type CCErrorData = {
  type: 'CC_SEND_ERROR';
  subtype: 'MISSING_EMAIL' | 'NOT_ASSIGNED' | 'BOUNCED';
  recipientType: 'ROLE' | 'EMAIL';
  recipientId: string;
  displayName: string;
  email?: string;
  itemType: 'MESSAGE' | 'TASK';
  itemTplId: string;
  /** Resolved timeline-item display title — mirrors `getIssueTitleText`'s
   * CC_SEND_ERROR branch in ParticipantDialog.tsx, precomputed here off
   * `tpl`/`itemTplId` at collection time rather than at render time. Empty
   * string if the referenced message/task template can't be found (deleted
   * since). */
  title: string;
  tpl: Doc<IExperienceTemplate>;
  instance: Doc<IExperience>;
  id: string;
  at: CCMissingEmailErrorData['at'];
};

export type AssigneeBounceErrorData = {
  type: 'ASSIGNEE_BOUNCE';
  email: string;
  displayName: string;
  bounceType: 'soft' | 'hard' | 'unknown';
  itemType: 'MESSAGE' | 'TASK';
  itemTplId: string;
  /** Resolved timeline-item display title — mirrors `getIssueTitleText`'s
   * ASSIGNEE_BOUNCE branch in ParticipantDialog.tsx, including the
   * TASK-bounce quirk where `itemTplId` is a task *instance* id, not a
   * template id (see `AssigneeBouncedErrorData.itemTplId`), precomputed
   * here at collection time rather than at render time. Empty string if
   * the referenced message/task can't be resolved. */
  title: string;
  tpl: Doc<IExperienceTemplate>;
  instance: Doc<IExperience>;
  id: string;
  at: AssigneeBouncedErrorData['at'];
};

export type ExperienceProblemData =
  | AutomationActionErrorData
  | SentItemErrorData
  | MissingActorErrorData
  | MissingVariableErrorData
  | InstanceErroWithData
  | CCErrorData
  | AssigneeBounceErrorData;

type ColumnName =
  | 'selector'
  | 'participant'
  | 'timelineItem'
  | 'action'
  | 'suggestedAction'
  | 'error'
  | 'date'
  | 'actions';

type D = ExperienceProblemData & { participant: Doc<IUser> };
type O = {
  selected: Set<string>;
  setSelected: (nextSelected: Set<string>) => void;
  selectAll: () => void;
  allSelected: boolean;
  onRetry: (d: ExperienceProblemData) => Promise<any>;
  onIgnore: (d: ExperienceProblemData) => Promise<any>;
};

type Column = IColumn<D, ColumnName, O>;

const Actions = ({ d, o }: { d: D; o: O }) => {
  const { t } = useUnsafeTranslation();

  return (
    <FlexContainer
      justifyContent="flex-end"
      onClick={(ev) => ev.stopPropagation()}
    >
      <ButtonWithPromise
        variant="outlined"
        color="primary"
        size="small"
        onClick={() => o.onIgnore(d)}
        pending={t('Ignore')}
      >
        {t('Ignore')}
      </ButtonWithPromise>
      {d.type !== 'MISSING_ACTOR' &&
        (d.type !== 'SENT_ITEM' || !!d.channels.length) && (
          <ButtonWithPromise
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => o.onRetry(d)}
            pending={t('Retry')}
          >
            {t('Retry')}
          </ButtonWithPromise>
        )}
    </FlexContainer>
  );
};

const getRowDate = (d: ExperienceProblemData) => {
  if (d.type === 'AUTOMATION') {
    return d.denormalized.doneAt || null;
  }
  if (d.type === 'SENT_ITEM') {
    return d.message.at || null;
  }
  if (d.type === 'MISSING_ACTOR' || d.type === 'MISSING_VAR') {
    return d.instance.data.startsAt || null;
  }
  if (d.type === 'CC_SEND_ERROR') {
    return d.at;
  }
  if (d.type === 'ASSIGNEE_BOUNCE') {
    return d.at;
  }
  if (Object.values(ErroDataType).includes(d.type)) {
    return d.at;
  }
  return null;
};

// getRowDate can return a Firestore Timestamp, a plain {seconds}/{_seconds} map, or a
// raw epoch-ms number — bounce/instanceError dates are NOT always Timestamps in v2
// (this is what crashed the issues bar: "x?.toMillis is not a function"). Normalise to
// millis via the shape-agnostic toMomentFromAny so sorting never calls .toMillis() on a
// non-Timestamp. (BE parity fix: those `at` fields should be written as Timestamps.)
const getRowDateMillis = (d: ExperienceProblemData): number => {
  const raw = getRowDate(d);
  return raw ? toMomentFromAny(raw).valueOf() || Infinity : Infinity;
};

const getTypeSortValue = (d: ExperienceProblemData) => {
  switch (d.type) {
    case 'MISSING_ACTOR':
      return 0;
    case 'SENT_ITEM':
      return 1;
    case 'AUTOMATION':
      return 2;
    case 'MISSING_VAR':
      return 3;
    case 'CANNOT_ASSIGN_SUPPORTING_ACTOR':
      return 4;
    case 'CC_SEND_ERROR':
      return 1;
    case 'ASSIGNEE_BOUNCE':
      return 1;
    default:
      return Infinity;
  }
};

const COLUMNS: Column[] = [
  {
    key: 'selector',
    head: (o) => {
      if (!o) {
        return null;
      }
      const indeterminate = !o.allSelected && !!o.selected.size;
      // Indeterminate looks the same as checked - why?
      return (
        <Checkbox
          checked={o.allSelected}
          indeterminate={indeterminate}
          onChange={(ev) => {
            const checked = ev.target.checked;
            if (checked) {
              o.selectAll();
            } else {
              o.setSelected(new Set());
            }
          }}
        />
      );
    },
    cell: (d, o) => {
      return (
        <Checkbox checked={o.selected.has(d.id)} style={{ marginTop: 2 }} />
      );
    },
    align: 'left',
    sortable: false,
    width: 30,
    flexGrow: 0
  },
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d) => {
      return d.participant && toFullName(d.participant.data);
    },
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'timelineItem',
    head: () => 'Timeline item',
    cell: (d) => {
      const count = (d as { occurrenceCount?: number }).occurrenceCount ?? 1;
      return (
        <FlexContainer alignItems="center" spacing={0.5}>
          <IssueTitle d={d} participant={d.participant} />
          {count > 1 && (
            <span
              title={`This issue occurred ${count} times`}
              className={css`
                font-size: 11px;
                font-weight: 600;
                padding: 1px 6px;
                border-radius: 10px;
                background: rgba(0, 0, 0, 0.08);
                color: rgba(0, 0, 0, 0.6);
                white-space: nowrap;
              `}
            >
              ×{count}
            </span>
          )}
        </FlexContainer>
      );
    },
    align: 'left',
    sortable: false,
    width: 150,
    flexGrow: 2
  },
  {
    key: 'error',
    head: () => 'Issue',
    cell: (d) => <IssueDetails d={d} />,
    align: 'left',
    sortable: false,
    width: 150,
    flexGrow: 3
  },
  {
    key: 'suggestedAction',
    head: () => 'Suggested action',
    cell: (d) => {
      if (d.type === 'AUTOMATION') {
        return (
          <SuggestedActionForError
            action={d.denormalized}
            instance={d.instance}
          />
        );
      }
      if (d.type === 'SENT_ITEM') {
        return (
          <SuggestedActionForSentItem
            errData={d}
            supportingActors={d.tpl.data.supportingActors}
          />
        );
      }
      if (d.type === 'MISSING_ACTOR' || d.type === 'MISSING_VAR') {
        return <SuggestedActionForMissingData errData={d} />;
      }
      if (d.type === 'CC_SEND_ERROR') {
        return <SuggestedActionForCCError errData={d} />;
      }
      if (d.type === 'ASSIGNEE_BOUNCE') {
        return <SuggestedActionForAssigneeBounce />;
      }
      if (Object.values(ErroDataType).includes(d.type)) {
        return (
          <SuggestedActionForInstanceSubtaskError
            errData={d}
            supportingActors={d.tpl.data.supportingActors}
          />
        );
      }
    },
    align: 'left',
    sortable: false,
    width: 150,
    flexGrow: 3
  },
  {
    key: 'date',
    head: () => 'Date',
    cell: (d) => {
      const date = getRowDate(d);
      return date ? formatDatePrecise(toMomentFromAny(date)) : null;
    },
    align: 'left',
    sortable: true,
    width: 100,
    flexGrow: 1
  },
  {
    key: 'actions',
    head: () => '',
    cell: (d, o) => <Actions d={d} o={o} />,
    align: 'right',
    sortable: false,
    width: 160,
    flexGrow: 0,
    showOverflowOnHover: false
  }
];

const rowToKey = (d: D) => d.id;

const getActorSortValue = (actorKey: string) => {
  return actorKey === PARTICIPANT_ACTOR_KEY
    ? '0'
    : actorKey === 'manager' || actorKey === 'mentor'
    ? '1'
    : actorKey;
};

const SORTERS: ItemSorters<D, O> = {
  participant: {
    key: 'participant',
    items: {
      sort: [
        (d) => toFullName(d.participant.data),
        (d) => Math.floor(getRowDateMillis(d) / ONE_MINUTE),
        (d) => getTypeSortValue(d),
        (d) => (d.type === 'SENT_ITEM' ? getActorSortValue(d.actor) : Infinity)
      ],
      dir: 'asc'
    }
  },

  date: {
    key: 'date',
    items: {
      sort: [
        (d) => Math.floor(getRowDateMillis(d) / ONE_MINUTE),
        (d) => toFullName(d.participant.data),
        (d) => getTypeSortValue(d),
        (d) => (d.type === 'SENT_ITEM' ? getActorSortValue(d.actor) : Infinity)
      ],
      dir: 'asc'
    }
  }
};

export const InstanceProblemsTable = ({
  tpl,
  ds,
  selected,
  setSelected,
  onRetry,
  onIgnore
}: {
  tpl: Doc<IExperienceTemplate>;
  ds: ExperienceProblemData[];
  selected: Set<string>;
  setSelected: (nextSelected: Set<string>) => void;
  onRetry: (d: ExperienceProblemData) => Promise<any>;
  onIgnore: (d: ExperienceProblemData) => Promise<any>;
}) => {
  const theme = useTheme();
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, O>>(
    SORTERS.date,
    SORTERS.date.items.dir
  );

  const userIds = useMemo(() => {
    return uniq(
      compact(ds.map((d) => getParticipantActor(d.instance)?.userId))
    );
  }, [ds]);

  const toRows = useCallback(
    (userMap: { [key: string]: Doc<IUser> }) =>
      ds.map<D>((d) => {
        const participantId = getParticipantActor(d.instance)?.userId || '';
        return {
          ...d,
          participant: userMap[participantId] || null
        };
      }),
    [ds]
  );

  const [rows, loading] = useMappedLoadingValue(
    useUserMapById(tpl.data.orgId, userIds),
    toRows,
    true
  );

  const otherProps = useMemo(() => {
    return {
      selected,
      setSelected,
      selectAll: () => setSelected(new Set(ds.map((d) => d.id))),
      allSelected: ds.every((d) => selected.has(d.id)),
      onRetry,
      onIgnore
    };
  }, [selected, setSelected, onRetry, onIgnore, ds]);

  const rowToClassName = useCallback(
    (d: D, o: O) => {
      if (o.selected.has(d.id)) {
        return css({ background: theme.custom.colors.blue.w100 });
      }
      return '';
    },
    [theme]
  );
  if (loading || !rows) {
    return <Loader height={300} />;
  }
  return (
    <RowsRenderer
      variant="plain"
      columns={COLUMNS}
      rows={rows}
      rowToKey={rowToKey}
      sorter={sortBy}
      sidePadding={false}
      sortDirection={direction}
      renderHead={true}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      onRowClick={(r) => {
        const nextSelected = new Set([...selected]);
        if (nextSelected.has(r.id)) {
          nextSelected.delete(r.id);
        } else {
          nextSelected.add(r.id);
        }
        setSelected(nextSelected);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowToClassName={rowToClassName}
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};

const DEACTIVATE_MISSING_VARIABLE_ISSUES = true;
export const collectInstanceErrors = async (
  tpl: Doc<IExperienceTemplate>,
  ds: Doc<IExperience>[],
  org: IOrganization
) => {
  const missingVarIssues = DEACTIVATE_MISSING_VARIABLE_ISSUES
    ? {}
    : await collectMissingVariables(tpl, ds, org);
  const problems = ds.reduce<ExperienceProblemData[]>((m, d) => {
    const automationProblems: ExperienceProblemData[] = collectAutomationIssues(
      tpl,
      d
    );
    const instanceErrors = collectInstanceIssues(d.data.instanceErrors, tpl, d);
    const ccErrors = collectCCIssues(tpl, d);
    const assigneeBounceErrors = collectAssigneeBounceIssues(tpl, d);

    const sentItemIssues = collectSentItemIssues(tpl, d);
    // const grouped = groupBy(sentItemIssues, (m) => {
    //   if (m.type === 'SENT_ITEM') {
    //     return m.id && m.message.provider && m.message.error?.type;
    //   }
    // });
    // Object.values(grouped).forEach((errData) => {
    //   // errData.map((err) => m.push(err));
    //   const recent = errData.reduce((prev, msg) => {
    //     // always of type 'MESSAGE'
    //     if (msg.type === 'SENT_ITEM' && prev.type === 'SENT_ITEM') {
    //       // return latest message issue only
    //       return prev.message.at > msg.message.at ? prev : msg;
    //     }
    //     return msg;
    //   });
    //   m.push(recent);
    // });

    // if (d.id === '1-obQy5rE-z') {
    //   console.log('ISSUES', {
    //     sentItemIssues,
    //     grouped,
    //     final: m.slice()
    //   });
    // }

    const missingSupportingActors = collectMissingSupportingActors(tpl, d);

    return [
      ...m,
      ...automationProblems,
      ...sentItemIssues,
      ...missingSupportingActors,
      ...instanceErrors,
      ...ccErrors,
      ...assigneeBounceErrors
    ];
  }, []);
  return [...problems, ...flatten(Object.values(missingVarIssues))];
};

// Exported so the retry-on-bounce handler in pages/Details/index.tsx can
// reuse the same channel-resolution logic SENT_ITEM rows use.
// See bd new-main-1gz.
export const getChannelsForSentItem = (
  tplChannels: ScheduleChannel[],
  msg: ConcreteExperienceMessage
): ScheduleChannel[] => {
  return tplChannels.filter((c) => {
    if (msg.provider === 'SLACK') {
      return c.type === ScheduleChannelType.SLACK;
    }
    if (msg.provider === 'TEAMS') {
      return c.type === ScheduleChannelType.MS_TEAMS;
    }
    if (msg.provider === 'TWILIO') {
      return c.type === ScheduleChannelType.SMS;
    }
    if (msg.provider === 'POSTMARK_EMAIL') {
      return (
        c.type === ScheduleChannelType.EMAIL && c.emailFrom === msg.emailFrom
      );
    }
    return false;
  });
};

